import { useEffect, useRef, useState } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { todayIST, makeReqTracker } from "@/lib/time";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Download, Filter, Trash2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const CLASSES = Array.from({ length: 12 }, (_, i) => String(i + 1));
const SECTIONS = ["A", "B", "C", "D"];

const CAT_STYLE = {
  late_coming: "bg-red-50 text-red-700 border-red-200",
  no_id_card: "bg-orange-50 text-orange-700 border-orange-200",
  improper_grooming: "bg-slate-100 text-slate-700 border-slate-200",
  no_homework: "bg-amber-50 text-amber-700 border-amber-200",
  forgot_diary: "bg-yellow-50 text-yellow-800 border-yellow-200",
  mobile_phone: "bg-purple-50 text-purple-700 border-purple-200",
  misbehavior: "bg-pink-50 text-pink-700 border-pink-200",
  other: "bg-blue-50 text-blue-700 border-blue-200",
};

export default function RecordsPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "editor";
  const [meta, setMeta] = useState({ categories: [] });
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ date_from: todayIST(), date_to: todayIST(), class_name: "", section: "", category: "" });
  const trackerRef = useRef(makeReqTracker());

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); }, []);

  const load = async () => {
    const reqId = trackerRef.current.next();
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    try {
      const { data } = await api.get("/inspections", { params });
      if (trackerRef.current.isLatest(reqId)) setRows(data);
    } catch (e) {
      if (trackerRef.current.isLatest(reqId)) toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

  const exportExcel = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    try {
      const res = await api.get(`/export/excel?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `carmel_inspections_${todayIST()}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try { await api.delete(`/inspections/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const labelFor = (v) => meta.categories.find((c) => c.value === v)?.label || v;

  return (
    <AppShell active="/records">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-carmel-gold mb-2">Ledger</p>
          <h2 className="font-serif text-2xl md:text-4xl tracking-tight">Inspection Records</h2>
          <p className="text-sm text-carmel-navy/60 mt-2">Filter, review, and export as Excel.</p>
        </div>
        <Button onClick={exportExcel} className="bg-carmel-navy hover:bg-carmel-navyMid" data-testid="export-excel-button">
          <FileSpreadsheet className="w-4 h-4 mr-2" /> Export Excel
        </Button>
      </div>

      <div className="bg-white border border-carmel-navy/10 rounded-lg p-4 mb-6 shadow-card">
        <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-widest text-carmel-navy/60"><Filter className="w-3.5 h-3.5" /> Filters</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} className="mt-1" data-testid="filter-date-from" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} className="mt-1" data-testid="filter-date-to" />
          </div>
          <div>
            <Label className="text-xs">Class</Label>
            <Select value={filters.class_name || "all"} onValueChange={(v) => setF("class_name", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="filter-class" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem>{CLASSES.map((c) => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Section</Label>
            <Select value={filters.section || "all"} onValueChange={(v) => setF("section", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="filter-section" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem>{SECTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Category</Label>
            <Select value={filters.category || "all"} onValueChange={(v) => setF("category", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="filter-category" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {meta.categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="bg-white border border-carmel-navy/10 rounded-lg overflow-x-auto shadow-card">
        <Table>
          <TableHeader className="bg-carmel-slateBg">
            <TableRow>
              <TableHead className="uppercase text-xs tracking-wider">Date</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Student</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Class</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Violations</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Remarks</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Recorded By</TableHead>
              {canEdit && <TableHead className="uppercase text-xs tracking-wider text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={canEdit ? 7 : 6} className="text-center py-16 text-carmel-navy/50">
                No records match these filters.
              </TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id} className="hover:bg-carmel-slateBg" data-testid={`record-row-${r.id}`}>
                <TableCell className="tabular-nums">{r.date}</TableCell>
                <TableCell className="font-medium">{r.student_name}</TableCell>
                <TableCell>{r.class_name}{r.section}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {r.categories.map((c) => (
                      <Badge key={c} variant="outline" className={`${CAT_STYLE[c] || ""} font-normal`}>{labelFor(c)}</Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-carmel-navy/60 max-w-xs truncate">{r.remarks || "—"}</TableCell>
                <TableCell className="text-carmel-navy/60">{r.created_by_name}</TableCell>
                {canEdit && (
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => remove(r.id)} data-testid={`record-delete-${r.id}`}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-carmel-navy/40 mt-4 flex items-center gap-2"><Download className="w-3 h-3" /> Excel export respects current filter selection.</p>
    </AppShell>
  );
}
